import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client'
import type { RssFeed, RssSource } from '../api/client'
import { useHasPermission } from '../api/session'
import { useItemQuery } from '../hooks/useItemQuery'
import { Card } from '../components/card/Card'
import { Button } from '../components/common/Button'
import { Input, Select } from '../components/common/Controls'
import { EmptyState, ErrorBox, Loading } from '../components/common/Feedback'
import { FilterBar, FilterEnd, FilterFixed } from '../components/common/FilterBar'
import { PermissionNotice } from '../components/common/PermissionNotice'
import s from './RssPage.module.scss'

/**
 * Schlagzeilen aus den hinterlegten RSS-Quellen.
 *
 * FLÜCHTIG. Die Einträge stehen in keiner Tabelle und sind beim nächsten
 * Neustart weg — sie sind Rohstoff für eine Entscheidung, nicht die
 * Entscheidung. Was zählt, wird zur Meldung.
 *
 * Deshalb auch kein Blättern und keine Sortierung über die Datenbank: Was da
 * ist, ist da, und es sind ein paar Dutzend Zeilen.
 */

const PERMISSION = 'rss:read'

const zeitpunkt = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'short', timeStyle: 'short',
})

// Mit Sekunden: Die Schonfrist ist eine Minute, und wer zweimal drückt,
// soll sehen, ob sich der Stand bewegt hat.
const stand = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'short', timeStyle: 'medium',
})

/** Die Stufen, wie sie in der Tabelle stehen. */
const STUFEN = [
  { wert: '1', text: 'nur Stufe 1' },
  { wert: '2', text: 'Stufe 1–2' },
  { wert: '3', text: 'Stufe 1–3' },
  { wert: '5', text: 'alle Stufen' },
]

export function RssPage() {
  const allowed = useHasPermission(PERMISSION)

  const [stufe, setStufe] = useState('5')
  // Der SCHLÜSSEL, nicht die Kennung: Er steht in der Adresse und
  // bleibt gültig, auch wenn die Quelle neu angelegt wird.
  const [quelle, setQuelle] = useState('')
  const [seit, setSeit] = useState('')
  // Wie oft der Knopf seit der letzten Filteränderung gedrückt wurde. Er
  // löst den Abruf neu aus, auch wenn kein Filter sich geändert hat — und
  // größer als null heißt: frisch verlangt, nicht aus dem Speicher. Ein
  // Filterwechsel setzt ihn zurück: Wer nur anders filtert, will nicht die
  // Quellen treffen, sondern sehen, was da ist.
  const [frisch, setFrisch] = useState(0)

  const { item, loading, error, denied } = useItemQuery<RssFeed>(
    useCallback((signal: AbortSignal) => api.rss({
      sources: quelle ? [quelle] : undefined,
      max_priority: stufe === '5' ? undefined : Number(stufe),
      since: seit ? `${seit}T00:00:00Z` : undefined,
      refresh: frisch > 0 || undefined,
    }, signal), [quelle, stufe, seit, frisch]),
    allowed)

  function filtern<T>(setzen: (wert: T) => void) {
    return (wert: T) => { setzen(wert); setFrisch(0) }
  }
  const quelleWaehlen = filtern(setQuelle)
  const stufeWaehlen = filtern(setStufe)
  const seitWaehlen = filtern(setSeit)

  const [quellen, setQuellen] = useState<RssSource[]>([])
  useEffect(() => {
    if (!allowed) return
    let aktuell = true
    api.rssSources()
      .then((antwort) => { if (aktuell) setQuellen(antwort) })
      .catch(() => { /* Ohne Liste zeigt die Seite eben keine Quellen an. */ })
    return () => { aktuell = false }
  }, [allowed])

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION}
                             subject="die Schlagzeilen" />
  }

  const probleme = Object.entries(item?.problems ?? {})

  return (
    <>
      <FilterBar>
        <FilterFixed>
          <Select label="Quelle" value={quelle}
                  disabled={quellen.length === 0}
                  onChange={(event) => quelleWaehlen(event.target.value)}>
            <option value="">alle Quellen</option>
            {quellen.filter((q) => q.active).map((q) => (
              <option key={q.key} value={q.key}>{q.name}</option>
            ))}
          </Select>
        </FilterFixed>
        <FilterFixed>
          <Select label="Wichtigkeit" value={stufe}
                  onChange={(event) => stufeWaehlen(event.target.value)}>
            {STUFEN.map((o) => (
              <option key={o.wert} value={o.wert}>{o.text}</option>
            ))}
          </Select>
        </FilterFixed>
        <FilterFixed>
          <Input label="Seit" type="date" value={seit}
                 onChange={(event) => seitWaehlen(event.target.value)} />
        </FilterFixed>
        <FilterEnd>
          {/*
            Von wann der Stand ist, steht ÜBER dem Knopf, wie die Beschriftung
            über einem Feld: Wer neu holen will, sieht zuerst, wie alt das ist,
            was er hat. Der älteste Abruf unter den gelieferten Quellen.
          */}
          <div className={s.stand}>
            {item?.fetched_at && (
              <span className={s.standText}>
                Stand {stand.format(new Date(item.fetched_at))}
              </span>
            )}
            <Button onClick={() => setFrisch((n) => n + 1)} disabled={loading}>
              {loading ? 'holt …' : 'Neu holen'}
            </Button>
          </div>
        </FilterEnd>
      </FilterBar>

      {error && <ErrorBox>{error}</ErrorBox>}

      {/*
        Quellen, die nicht geliefert haben, werden BENANNT und nicht
        verschwiegen: Sonst hält jemand eine unvollständige Übersicht für
        vollständig. Es ist kein Fehler des Abrufs — der Rest kam ja.
      */}
      {probleme.length > 0 && (
        <Card span={12}>
          <p className={s.problemKopf}>
            {probleme.length === 1
              ? 'Eine Quelle hat nicht geliefert:'
              : `${probleme.length} Quellen haben nicht geliefert:`}
          </p>
          <ul className={s.probleme}>
            {probleme.map(([name, grund]) => (
              <li key={name}><b>{name}</b> {grund}</li>
            ))}
          </ul>
        </Card>
      )}

      <Card flush span={12}
            meta={item ? `${item.entries.length} Schlagzeilen` : undefined}>
        {loading && !item && <Loading>holt die Feeds …</Loading>}

        {item && item.entries.length === 0 && !loading && (
          <EmptyState title="keine Schlagzeilen">
            {quellen.length === 0
              ? 'Es ist noch keine Quelle hinterlegt.'
              : 'Keine der Quellen hat etwas im gewählten Zeitraum.'}
          </EmptyState>
        )}

        <ul className={s.liste}>
          {item?.entries.map((eintrag, i) => (
            <li key={`${eintrag.source_id}-${i}`} className={s.eintrag}>
              <div className={s.kopf}>
                <span className={s.quelle}>{eintrag.source_name}</span>
                <span className={s.wann}>
                  {eintrag.published_at
                    ? zeitpunkt.format(new Date(eintrag.published_at))
                    : <span className={s.ohne}>ohne Datum</span>}
                </span>
              </div>
              {eintrag.link ? (
                <a className={s.titel} href={eintrag.link}
                   target="_blank" rel="noopener noreferrer">
                  {eintrag.title}
                </a>
              ) : (
                <span className={s.titel}>{eintrag.title}</span>
              )}
              {eintrag.description && (
                <p className={s.text}>{eintrag.description}</p>
              )}
            </li>
          ))}
        </ul>
      </Card>

      {item && item.from_cache.length > 0 && (
        // Wie frisch die Übersicht ist, gehört dazu: Beim Öffnen gilt ein
        // Abruf fünfzehn Minuten, und wer das nicht weiß, wundert sich über
        // ausbleibende Neuigkeiten. Nach „Neu holen" bleibt nur, was der
        // Server in der letzten Minute schon geholt hat — das soll nicht
        // wie ein Knopf ohne Wirkung aussehen.
        <p className={s.fussnote}>
          {frisch > 0
            ? `Gerade erst geholt, deshalb aus dem Zwischenspeicher: ${item.from_cache.join(', ')}. Eine Quelle wird höchstens einmal je Minute abgerufen.`
            : `Aus dem Zwischenspeicher: ${item.from_cache.join(', ')}. Ein Abruf gilt fünfzehn Minuten.`}
        </p>
      )}
    </>
  )
}
